import Link from 'next/link';
import { useTranslation } from 'next-i18next';
import { overlay } from 'overlay-kit';
import { logger } from '@zep/core-logger';
import { useLocale, useViewport } from '@/hooks';
import { Button, DialogBody, DialogHeader, ResponsiveDialog } from '@/ui_v3';
import { cn } from '@/utils';
import { LinkContentToClassroomSection } from './LinkContentToClassroomSection';
import { ShareContent } from './ShareContent';
interface ContentShareModalCommonProps {
  title: string;
  contentId: string;
  contentOwnerId: string;
  shareUrl: string;
  hideFooter?: boolean;
  /**
   * 클래스 상세 페이지처럼 이미 특정 클래스 컨텍스트에 있는 곳에서 열린 공유 모달은
   * 클래스 연결 UI 를 노출하지 않는다.
   */
  hideClassroomLinking?: boolean;
  onClose: () => void;
}
interface QuizContentShareModalProps extends ContentShareModalCommonProps {
  contentType: 'QUIZSET';
  enterCode: number;
  spaceHashId: string;
}
interface BoardContentShareModalProps extends ContentShareModalCommonProps {
  contentType: 'BOARD';
  enterCode: number;
  spaceHashId?: never;
}
export type ContentShareModalProps = QuizContentShareModalProps | BoardContentShareModalProps;
type ShowShareModalPayload = (Omit<QuizContentShareModalProps, 'onClose'> & {
  trackLocation: string;
}) | (Omit<BoardContentShareModalProps, 'onClose'> & {
  trackLocation: string;
});
export function ContentShareModal(props: ContentShareModalProps) {
  const {
    locale
  } = useLocale();
  const {
    isMobile
  } = useViewport();
  const {
    t
  } = useTranslation(['common']);
  const canShowLinkContentToClassroom = !props.hideClassroomLinking;
  return <ResponsiveDialog size={isMobile || canShowLinkContentToClassroom ? 'lg' : 'md'} onClose={props.onClose} className={canShowLinkContentToClassroom ? 'mobile-lg:!h-[583px]' : undefined} data-sentry-element="ResponsiveDialog" data-sentry-component="ContentShareModal" data-sentry-source-file="ContentShareModal.tsx">
      <DialogHeader title={t('shareModal.title')} hasCloseButton={true} onClose={props.onClose} data-sentry-element="DialogHeader" data-sentry-source-file="ContentShareModal.tsx" />
      <DialogBody className={cn('flex min-h-0 flex-col items-center gap-md overflow-auto px-md mobile-lg:px-lg', canShowLinkContentToClassroom && 'overflow-hidden mobile-lg:min-h-0 mobile-lg:flex-1 mobile-lg:flex-row mobile-lg:items-stretch mobile-lg:gap-lg')} data-sentry-element="DialogBody" data-sentry-source-file="ContentShareModal.tsx">
        <div className={cn('relative z-0 flex w-full min-w-0 flex-col items-center gap-md pb-lg', canShowLinkContentToClassroom && 'min-h-0 flex-1 pb-0 mobile-lg:w-[450px] mobile-lg:flex-none mobile-lg:shrink-0 mobile-lg:pb-lg')}>
          <ShareContent title={props.title} enterCode={props.enterCode} shareUrl={props.shareUrl} data-sentry-element="ShareContent" data-sentry-source-file="ContentShareModal.tsx" />
          {props.contentType === 'QUIZSET' && !props.hideFooter && !canShowLinkContentToClassroom && <Link href={props.shareUrl} target="_blank" className="w-full">
                <Button onClick={() => {
            logger.track('enter_space', {
              locale
            });
            logger.track('enter_play', {
              location: 'shareModal',
              spaceId: props.spaceHashId,
              isAuthor: true
            });
          }} className="w-full">
                  {t('global.enter.button')}
                </Button>
              </Link>}
        </div>
        {canShowLinkContentToClassroom && <LinkContentToClassroomSection contentId={props.contentId} contentType={props.contentType} />}
      </DialogBody>
    </ResponsiveDialog>;
}
export const showShareModal = (payload: ShowShareModalPayload) => {
  logger.track('enter_share', {
    location: payload.trackLocation,
    contentId: payload.contentId,
    contentType: payload.contentType,
    quizSetId: payload.contentId
  });
  if (payload.contentType === 'QUIZSET') {
    const {
      trackLocation: _trackLocation,
      ...modalProps
    } = payload;
    overlay.open(({
      unmount
    }) => <ContentShareModal {...modalProps} onClose={unmount} />);
    return;
  }
  const {
    trackLocation: _trackLocation,
    ...modalProps
  } = payload;
  overlay.open(({
    unmount
  }) => <ContentShareModal {...modalProps} onClose={unmount} />);
};